import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Card } from '@ovComponents/2-component/card/card';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useMemo, useState } from 'react';
import {
  BORDERED_BOX_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
} from '../styles';
import type { PortfolioReport, PreFlightResult } from '../types';
import {
  calculateDrift,
  formatMoney,
  getPortfolioName,
  getPortfolioStatus,
  runPreFlightChecks,
} from '../utils';

type ReviewAndCommitProps = {
  selectedPortfolios: PortfolioReport[];
  operationType: 'optimize' | 'rebalance' | 'generateTrades';
  onCommit: (portfolioIds: string[], auditNote: string) => void;
  onCancel: () => void;
};

const operationLabels = {
  optimize: 'Portfolio Optimization',
  rebalance: 'Rebalance',
  generateTrades: 'Generate Trades',
};

const ReviewAndCommit = ({
  selectedPortfolios,
  operationType,
  onCommit,
  onCancel,
}: ReviewAndCommitProps) => {
  const { sys } = useThemeTokens();
  const [step, setStep] = useState<'review' | 'confirm'>('review');
  const [auditNote, setAuditNote] = useState('');

  const summary = useMemo(() => {
    let totalMarketValue = 0,
      totalCashAvailable = 0,
      portfoliosWithDrift = 0,
      portfoliosWithLiquidation = 0,
      portfoliosWithTransfer = 0,
      totalPendingTrades = 0;
    selectedPortfolios.forEach((p) => {
      totalMarketValue += p.marketValueCents || 0;
      totalCashAvailable += p.cashAvailableForTradeCents || 0;
      if (p.driftRebalanceRequired) portfoliosWithDrift++;
      if (p.liquidateRebalanceRequired) portfoliosWithLiquidation++;
      if (p.transferRebalanceRequired) portfoliosWithTransfer++;
      totalPendingTrades += p.pendingSubTradeRequests || 0;
    });
    return {
      totalMarketValue,
      totalCashAvailable,
      portfoliosWithDrift,
      portfoliosWithLiquidation,
      portfoliosWithTransfer,
      totalPendingTrades,
    };
  }, [selectedPortfolios]);

  const preFlightResults: PreFlightResult[] = useMemo(
    () => selectedPortfolios.map((p) => runPreFlightChecks(p, [])),
    [selectedPortfolios],
  );

  const hasErrors = preFlightResults.some((r) => r.overallSeverity === 'error');
  const hasWarnings = preFlightResults.some(
    (r) => r.overallSeverity === 'warning',
  );
  const passCount = preFlightResults.filter(
    (r) => r.overallSeverity === 'pass',
  ).length;
  const warnCount = preFlightResults.filter(
    (r) => r.overallSeverity === 'warning',
  ).length;
  const errorCount = preFlightResults.filter(
    (r) => r.overallSeverity === 'error',
  ).length;

  const handleCommit = () =>
    onCommit(
      selectedPortfolios.map((p) => p.id),
      auditNote,
    );

  const SUMMARY_CARDS = [
    {
      label: 'Total Market Value',
      value: formatMoney(summary.totalMarketValue, { compact: true }),
    },
    {
      label: 'Cash Available',
      value: formatMoney(summary.totalCashAvailable, { compact: true }),
    },
    {
      label: 'Requiring Action',
      value:
        summary.portfoliosWithDrift +
        summary.portfoliosWithLiquidation +
        summary.portfoliosWithTransfer,
      subtext: `${summary.portfoliosWithDrift} drift, ${summary.portfoliosWithLiquidation} liq, ${summary.portfoliosWithTransfer} xfer`,
    },
    { label: 'Pending Trades', value: summary.totalPendingTrades },
  ];

  const STATUS_ITEMS = [
    {
      count: passCount,
      Icon: CheckCircleOutlineIcon,
      label: 'passed',
      color: sys.color.positive,
    },
    {
      count: warnCount,
      Icon: WarningAmberIcon,
      label: 'warnings',
      color: sys.color.warning,
    },
    {
      count: errorCount,
      Icon: ErrorOutlineIcon,
      label: 'errors',
      color: sys.color.negative,
    },
  ].filter(({ count }) => count > 0);

  return (
    <Card
      sx={{
        border: '2px solid',
        borderColor: hasErrors
          ? sys.color.negative
          : hasWarnings
            ? sys.color.warning
            : sys.color.primary,
      }}
    >
      <Box p={3}>
        <Box mb={3}>
          <Typography variant='titleLarge' sx={{ mb: 0.5 }}>
            Review & Commit: {operationLabels[operationType]}
          </Typography>
          <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
            {selectedPortfolios.length} portfolio
            {selectedPortfolios.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>

        {step === 'review' && (
          <>
            <Box
              display='grid'
              gridTemplateColumns='repeat(4, 1fr)'
              gap={1.5}
              mb={3}
            >
              {SUMMARY_CARDS.map(({ label, value, subtext }) => (
                <Box
                  key={label}
                  p={1.5}
                  sx={{
                    ...BORDERED_BOX_SX,
                    bgcolor: sys.color.background,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant='bodySmall'
                    sx={{ ...SECTION_HEADER_SX, fontWeight: 600, mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Typography variant='titleMedium'>{value}</Typography>
                  {subtext != null && (
                    <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
                      {subtext}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            <Box mb={3}>
              <Typography variant='titleMedium' sx={{ mb: 1 }}>
                Pre-Flight Validation
              </Typography>
              <Box display='flex' gap={2} mb={1.5}>
                {STATUS_ITEMS.map(({ count, Icon, label, color }) => (
                  <Box key={label} display='flex' alignItems='center' gap={0.5}>
                    <Icon sx={{ fontSize: 14, color }} />
                    <Typography variant='bodySmall' sx={{ color }}>
                      {count} {label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {preFlightResults
                .filter((r) => r.overallSeverity !== 'pass')
                .map((result) => {
                  const portfolio = selectedPortfolios.find(
                    (p) => p.id === result.portfolioId,
                  );
                  return (
                    <Box
                      key={result.portfolioId}
                      sx={{
                        p: 1,
                        mb: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor:
                          result.overallSeverity === 'error'
                            ? sys.color.negativeVariant
                            : sys.color.warningVariant,
                      }}
                    >
                      <Box display='flex' alignItems='center' gap={1}>
                        {result.overallSeverity === 'error' ? (
                          <ErrorOutlineIcon
                            sx={{ fontSize: 14, color: sys.color.negative }}
                          />
                        ) : (
                          <WarningAmberIcon
                            sx={{ fontSize: 14, color: sys.color.warning }}
                          />
                        )}
                        <Typography
                          variant='bodySmall'
                          weight='bold'
                          sx={{ flex: 1 }}
                        >
                          {portfolio
                            ? getPortfolioName(portfolio)
                            : result.portfolioId}
                        </Typography>
                        <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
                          {result.checks
                            .filter((c) => c.severity !== 'pass')
                            .map((c) => c.name)
                            .join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}

              {preFlightResults.every((r) => r.overallSeverity === 'pass') && (
                <Box
                  p={1.5}
                  sx={{
                    bgcolor: sys.color.positiveVariant,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: sys.color.positive,
                  }}
                >
                  <Box display='flex' alignItems='center' gap={1}>
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 16, color: sys.color.positive }}
                    />
                    <Typography
                      variant='bodySmall'
                      sx={{ color: sys.color.positive }}
                    >
                      All portfolios passed pre-flight checks
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box mb={3}>
              <Typography variant='titleMedium' sx={{ mb: 1 }}>
                Selected Portfolios
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {selectedPortfolios.map((p) => {
                  const drift = calculateDrift(p);
                  const status = getPortfolioStatus(p);
                  return (
                    <Box
                      key={p.id}
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                      py={0.5}
                      px={1}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: sys.color.outlineVariant,
                        '&:hover': { bgcolor: sys.color.neutralHover },
                      }}
                    >
                      <Box flex={1}>
                        <Typography variant='bodySmall'>
                          {getPortfolioName(p)}
                        </Typography>
                      </Box>
                      <Box display='flex' gap={2} alignItems='center'>
                        <Typography variant='bodySmall' sx={{}}>
                          {formatMoney(p.marketValueCents, { compact: true })}
                        </Typography>
                        {drift && (
                          <Typography
                            variant='bodySmall'
                            sx={{
                              color:
                                drift > 5
                                  ? sys.color.negative
                                  : sys.color.onSurfaceVariant,
                            }}
                          >
                            {drift.toFixed(1)}% drift
                          </Typography>
                        )}
                        <Chip text={status.label} small />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box display='flex' justifyContent='flex-end' gap={1}>
              <Button
                label='Cancel'
                variant='outlined'
                size='sm'
                onClick={onCancel}
              />
              <Button
                label={
                  hasErrors ? 'Resolve Errors First' : 'Proceed to Confirm'
                }
                variant='filled'
                size='sm'
                onClick={() => setStep('confirm')}
                disabled={hasErrors}
              />
            </Box>
          </>
        )}

        {step === 'confirm' && (
          <>
            <Box
              mb={3}
              p={2}
              sx={{
                bgcolor: sys.color.warningVariant,
                border: '2px solid',
                borderColor: sys.color.warning,
                borderRadius: 1,
              }}
            >
              <Typography
                variant='titleMedium'
                sx={{ mb: 1, color: sys.color.warning }}
              >
                Confirm {operationLabels[operationType]}
              </Typography>
              <Typography variant='bodySmall' sx={{ mb: 1 }}>
                You are about to run{' '}
                <strong>{operationLabels[operationType]}</strong> on{' '}
                <strong>{selectedPortfolios.length}</strong> portfolio
                {selectedPortfolios.length !== 1 ? 's' : ''} with a combined
                market value of{' '}
                <strong>
                  {formatMoney(summary.totalMarketValue, { compact: true })}
                </strong>
                .
              </Typography>
              {hasWarnings && (
                <Typography
                  variant='bodySmall'
                  sx={{ color: sys.color.warning, fontWeight: 600 }}
                >
                  Note: {warnCount} portfolio{warnCount !== 1 ? 's' : ''} have
                  warnings. Review above before proceeding.
                </Typography>
              )}
            </Box>

            <Box mb={3}>
              <TextField
                label='Audit Note (optional)'
                placeholder='Reason for this operation...'
                value={auditNote}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAuditNote(e.target.value)
                }
                fullWidth
                multiline
              />
            </Box>

            <Box display='flex' justifyContent='flex-end' gap={1}>
              <Button
                label='Back'
                variant='outlined'
                size='sm'
                onClick={() => setStep('review')}
              />
              <Button
                label={`Commit ${operationLabels[operationType]}`}
                variant='filled'
                size='sm'
                onClick={handleCommit}
              />
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export default ReviewAndCommit;
